import React, { useMemo } from 'react';
import {
    Modal,
    Button,
    Tabs,
    Box,
} from '@amzn/awsui-components-react';
import { CoachingRecord } from '../types';

type ByTheNumbersProps = {
    coachings: CoachingRecord[];
    userPermissions: Set<string>;
};

const ByTheNumbers: React.FC<ByTheNumbersProps> = ({ coachings, userPermissions }) => {
    const [visible, setVisible] = React.useState(false);

    const { totalSubmissions, statusCount, categoryCount, reasonCount, actionCount } = useMemo(() => {
        const statusMap: Record<string, number> = {};
        const categoryMap: Record<string, number> = {};
        const reasonMap: Record<string, number> = {};
        const actionMap: Record<string, number> = {};

        coachings.forEach((c) => {
            const s = c.status || 'N/A';
            statusMap[s] = (statusMap[s] ?? 0) + 1;

            const cat = c.category || 'N/A';
            categoryMap[cat] = (categoryMap[cat] ?? 0) + 1;

            const r = c.reason || 'N/A';
            reasonMap[r] = (reasonMap[r] ?? 0) + 1;

            const a = c.action || 'N/A';
            actionMap[a] = (actionMap[a] ?? 0) + 1;
        });
        return {
            totalSubmissions: coachings.length,
            statusCount: statusMap,
            categoryCount: categoryMap,
            reasonCount: reasonMap,
            actionCount: actionMap,
        };
    }, [coachings]);

    const renderStatisticBlock = (title: string, value: number | string) => {
        return (
            <Box margin={{ vertical: 'xs' }}>
                <Box variant="awsui-key-label">{title}</Box>
                <Box>{value}</Box>
            </Box>
        );
    };

    return (
        <>
            <Button variant='primary' onClick={() => setVisible(true)}>
                By the Numbers
            </Button>
            <Modal
                visible={visible}
                size='large'
                onDismiss={() => setVisible(false)}
                header='By the Numbers'
            >
                <Tabs
                    tabs={[
                        {
                            label: 'Totals',
                            id: 'totals',
                            content: (
                                <Box padding="m">
                                    {renderStatisticBlock('Total Submissions', totalSubmissions)}
                                    {Object.entries(statusCount).map(([k, v]) =>
                                        <React.Fragment key={`status-${k}`}>
                                            {renderStatisticBlock(k, v)}
                                        </React.Fragment>
                                    )}
                                </Box>
                            ),
                        },
                        {
                            label: 'Category Breakdown',
                            id: 'category',
                            content: (
                                <Box padding="m">
                                    {Object.entries(categoryCount).map(([k, v]) =>
                                        <React.Fragment key={`status-${k}`}>
                                            {renderStatisticBlock(k, v)}
                                        </React.Fragment>
                                    )}
                                </Box>
                            ),
                        },
                        {
                            label: 'Reason Breakdown',
                            id: 'reason',
                            content: (
                                <Box padding="m">
                                    {Object.entries(reasonCount).map(([k, v]) =>
                                        <React.Fragment key={`status-${k}`}>
                                            {renderStatisticBlock(k, v)}
                                        </React.Fragment>
                                    )}
                                </Box>
                            ),
                        },
                        {
                            label: 'Action Breakdown',
                            id: 'action',
                            content: (
                                <Box padding="m">
                                    {Object.entries(actionCount).map(([k, v]) =>
                                        <React.Fragment key={`status-${k}`}>
                                            {renderStatisticBlock(k, v)}
                                        </React.Fragment>
                                    )}
                                </Box>
                            ),
                        },
                    ]}
                />
            </Modal>
        </>
    );
};

export default ByTheNumbers;
